'use client';

import { c as _c } from "react/compiler-runtime";
import { usePrefetchGetUser } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { userIdentityKey } from '@/components/profile/user-identity-key';
import { DataTable } from '@/components/ui/data-table';
import { getProfileHref } from '@/lib/profile/profile-href';
import { PORTFOLIO_USER_PROFILE_FIELDS } from '@/lib/sdk/projections';
import type { MarketTradersSort, MarketTraderViewModel } from './trader-row';
import { TraderRowCells } from './trader-row-cells';
import { MARKET_TRADERS_MOBILE_ROW_CLASS } from './trader-row-layout';
interface MarketTradersTableProps {
  entries: ReadonlyArray<MarketTraderViewModel>;
  currentWalletAddress: string | null;
  sortBy: MarketTradersSort;
  sortOrder: 'asc' | 'desc';
  onSortChange: (sort: MarketTradersSort) => void;
  onSortOrderChange: (order: 'asc' | 'desc') => void;
}
export function MarketTradersTable(t0) {
  const $ = _c(39);
  const {
    entries,
    currentWalletAddress,
    sortBy,
    sortOrder,
    onSortChange,
    onSortOrderChange
  } = t0;
  const t = useTranslations("market.traders");
  const prefetchUser = usePrefetchGetUser();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <DataTable.RankColumn />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== t) {
    t2 = t("trader");
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <DataTable.IdentityColumn header={t2} />;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t) {
    t4 = t("volume");
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = <DataTable.SortableColumn sortKey="volume" header={t4} align="right" className="hidden w-24 shrink-0 xl:table-cell" hideOnMobile={true} />;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t) {
    t6 = t("unrealized_pnl");
    $[9] = t;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t6) {
    t7 = <DataTable.SortableColumn sortKey="unrealized" header={t6} align="right" className="hidden w-24 shrink-0 xl:table-cell" hideOnMobile={true} />;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t) {
    t8 = t("realized_pnl");
    $[13] = t;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t8) {
    t9 = <DataTable.SortableColumn sortKey="realized" header={t8} align="right" className="hidden w-24 shrink-0 xl:table-cell" hideOnMobile={true} />;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t) {
    t10 = t("total_pnl");
    $[17] = t;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10) {
    t11 = <DataTable.SortableColumn sortKey="total" header={t10} align="right" className="w-24 shrink-0 xl:w-32" />;
    $[19] = t10;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t11 || $[22] !== t3 || $[23] !== t5 || $[24] !== t7 || $[25] !== t9) {
    t12 = <DataTable.Header>{t1}{t3}{t5}{t7}{t9}{t11}</DataTable.Header>;
    $[21] = t11;
    $[22] = t3;
    $[23] = t5;
    $[24] = t7;
    $[25] = t9;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== currentWalletAddress || $[28] !== prefetchUser || $[29] !== t) {
    t13 = <DataTable.Body>{entry => {
        const entryUserId = entry.trader.userId ?? "";
        const isUser = currentWalletAddress !== null && entry.trader.walletAddress === currentWalletAddress;
        const rowKey = entry.participationId.length > 0 ? entry.participationId : userIdentityKey(entry.trader, `anonymous-rank-${entry.rank}`);
        const href = entryUserId.length > 0 ? getProfileHref(entryUserId) : undefined;
        const prefetch = function prefetch() {
          if (entryUserId.length === 0) {
            return;
          }
          prefetchUser({
            identifier: entryUserId,
            fields: [...PORTFOLIO_USER_PROFILE_FIELDS]
          });
        };
        return <DataTable.Row key={rowKey} isCurrentUser={isUser} {...href !== undefined ? {
          href
        } : {}} onMouseEnter={prefetch} onFocus={prefetch} onPointerDown={prefetch} ariaLabel={t("aria_view_profile")} data-testid="market-traders-row" className={MARKET_TRADERS_MOBILE_ROW_CLASS}><TraderRowCells entry={entry} isUser={isUser} /></DataTable.Row>;
      }}</DataTable.Body>;
    $[27] = currentWalletAddress;
    $[28] = prefetchUser;
    $[29] = t;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== entries || $[32] !== onSortChange || $[33] !== onSortOrderChange || $[34] !== sortBy || $[35] !== sortOrder || $[36] !== t12 || $[37] !== t13) {
    t14 = <DataTable entries={entries} sortBy={sortBy} sortOrder={sortOrder} onSortChange={onSortChange} onSortOrderChange={onSortOrderChange} panel="borderless" cardUntil="xl">{t12}{t13}</DataTable>;
    $[31] = entries;
    $[32] = onSortChange;
    $[33] = onSortOrderChange;
    $[34] = sortBy;
    $[35] = sortOrder;
    $[36] = t12;
    $[37] = t13;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  return t14;
}